import { useAuth } from '@/auth/auth-context'
import { useSite } from '@/hooks/use-site'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@blogging/ui'
import { Lock } from 'lucide-react'
import { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

interface ContentEditGuardProps {
  children: ReactNode
  contentOwnerId?: string | null
  contentType: 'post' | 'page'
  isLoading?: boolean
}

export function ContentEditGuard({
  children,
  contentOwnerId,
  contentType,
  isLoading,
}: ContentEditGuardProps) {
  const { canManageAllContent } = useSite()
  const { user } = useAuth()
  const { siteId } = useParams()
  const navigate = useNavigate()

  // Show loading state while checking permissions
  if (isLoading) {
    return null
  }

  // For new content (no contentOwnerId), anyone can create
  if (!contentOwnerId) {
    return <>{children}</>
  }

  // Check if user can edit this specific content
  const canEdit = canManageAllContent || contentOwnerId === user?.id

  if (!canEdit) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto mb-4">
              <Lock className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold">Cannot Edit {contentType}</CardTitle>
            <CardDescription>
              You don't have permission to edit this {contentType}. Only the
              author and editors/admins can edit it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
              <p className="font-medium mb-2">Why am I seeing this?</p>
              <p className="text-xs">
                This {contentType} was created by another user. As an author,
                you can only edit your own content. Editors and administrators
                can edit all content.
              </p>
            </div>
            <Button
              onClick={() => navigate(`/${siteId}/${contentType}s`)}
              className="w-full"
            >
              Go to {contentType === 'post' ? 'Posts' : 'Pages'}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
